import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBar from '../components/AppBar';
import { usePaginatedList } from '../hooks/usePaginatedList';
import { Chip, EmptyState, InfiniteSentinel, SearchBar, Skeletons } from '../components/kit';
import * as formsApi from '../api/bookingForms';

// Filters send the raw DB enum; the labels say what the state means to an AGENT.
//
// APPROVED is filed under "Issued" — the value is a leftover from the HQ approval step,
// which no longer exists, and is kept in the database only because renaming it would
// have meant migrating every row to change a word on a chip. SIGNED reads plainly as
// "Signed" now: it used to say "HQ review", which was true when a fully-signed form
// went into a queue, and is a lie now that the agent issues it themselves.
//
// PENDING_APPROVAL and REJECTED are omitted rather than offered — nothing writes either,
// so both filters would promise a list that is always empty.
const STATUS_FILTERS = [
  { key: '', label: 'All' },
  { key: 'DRAFT', label: 'Draft' },
  { key: 'PENDING_SIGNING', label: 'Signing' },
  { key: 'SIGNED', label: 'Signed' },
  { key: 'APPROVED', label: 'Issued' },
  { key: 'CANCELLED', label: 'Cancelled' },
];

export default function FormsPage() {
  const nav = useNavigate();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const params = useMemo(() => ({ status: status || undefined, search: search || undefined }), [status, search]);
  // paginated list — sends { page, limit, status, search } and appends pages on scroll
  const list = usePaginatedList(formsApi.listBookingForms, { limit: 10, params });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <AppBar back title="Booking forms" sub="OTP · OTR · ALS · ALR" />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <SearchBar onSearch={setSearch} placeholder="Search ref or property address…" />
        <div className="scrx" style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
          {STATUS_FILTERS.map((f) => {
            const active = f.key === status;
            return (
              <div key={f.key} onClick={() => setStatus(f.key)} style={{ flex: 'none', fontSize: 12.5, fontWeight: 600, padding: '6px 13px', borderRadius: 999, cursor: 'pointer', background: active ? 'var(--primary)' : 'var(--surface2)', color: active ? '#fff' : 'var(--muted)', border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}` }}>
                {f.label}
              </div>
            );
          })}
        </div>
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {list.loading ? (
          <Skeletons n={4} />
        ) : list.empty ? (
          <EmptyState title="No booking forms" sub={search || status ? 'Nothing matches your search or filter.' : 'Create an OTP, OTR, ALS or ALR to get started.'} />
        ) : (
          <>
            {list.items.map((f, i) => (
              <div key={f.id} data-tour={i === 0 ? 'forms-row' : undefined} onClick={() => nav(`/forms/${f.id}`)} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '15px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, cursor: 'pointer' }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: 'var(--chip)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700, flex: 'none', letterSpacing: '.03em' }}>
                  {f.form_type}
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'ui-monospace,monospace' }}>{f.form_ref}</span>
                    <Chip label={f.status} />
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {f.form_type} · {f.property}
                  </div>
                </div>
              </div>
            ))}
            <InfiniteSentinel hasMore={list.hasMore} loading={list.loadingMore} onReach={list.loadMore} />
          </>
        )}
        <div style={{ height: 66 }} />
      </div>

      <div data-tour="forms-fab" onClick={() => nav('/forms/new')} style={{ position: 'absolute', right: 18, bottom: 18, width: 54, height: 54, borderRadius: 50, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, cursor: 'pointer', boxShadow: '0 8px 20px color-mix(in srgb,var(--primary) 45%,transparent)', zIndex: 5 }}>＋</div>
    </div>
  );
}
